(() => {
  const ready = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  ready(() => {
    const header = document.querySelector('[data-elfine-header]');
    const toggle = document.querySelector('.elfine-menu-toggle');
    const nav = document.querySelector('#ElfineNav');

    const setMenu = (open) => {
      if (!toggle || !nav) return;
      toggle.classList.toggle('is-open', open);
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.documentElement.style.overflow = open ? 'hidden' : '';
    };

    toggle?.addEventListener('click', () => setMenu(toggle.getAttribute('aria-expanded') !== 'true'));
    nav?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setMenu(false); });

    if (header?.classList.contains('elfine-header--overlay')) {
      const scrollContainer = document.querySelector('.page-wrapper');
      const scrollTarget = scrollContainer || window;
      const updateHeader = () => {
        const scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
        header.classList.toggle('elfine-header--scrolled', scrollTop > 64);
      };
      updateHeader();
      scrollTarget.addEventListener('scroll', updateHeader, { passive: true });
    }

    document.querySelectorAll('[data-elfine-slider]').forEach((slider) => {
      const slides = [...slider.querySelectorAll('[data-slide]')];
      const dots = [...slider.querySelectorAll('[data-slider-dot]')];
      if (slides.length < 2) return;
      let index = 0;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      const stop = () => { window.clearInterval(timer); timer = undefined; };
      const start = () => { stop(); if (!document.hidden) timer = window.setInterval(() => show(index + 1), 3000); };
      slider.querySelector('[data-slider-prev]')?.addEventListener('click', () => { show(index - 1); start(); });
      slider.querySelector('[data-slider-next]')?.addEventListener('click', () => { show(index + 1); start(); });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else start(); });
      show(0);
      start();
    });

    document.querySelectorAll('.elfine-product-card, .product-information__media, .product-details, .product-recommendations').forEach((item) => {
      item.setAttribute('data-reveal', '');
    });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealItems = [...document.querySelectorAll('[data-reveal]')];
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-revealed'));
    } else {
      const revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { root: document.querySelector('.page-wrapper') || null, threshold: 0.12, rootMargin: '0px 0px -55px' });
      revealItems.forEach((item) => revealObserver.observe(item));
    }

    const toast = document.querySelector('[data-cart-toast]');
    let toastTimer;
    const showCartToast = () => {
      if (!toast) return;
      window.clearTimeout(toastTimer);
      toast.classList.add('is-visible');
      toast.setAttribute('aria-hidden', 'false');
      toastTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        toast.setAttribute('aria-hidden', 'true');
      }, 3200);
    };

    const updateCartCount = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count < 1;
      });
      const cartLink = document.querySelector('.elfine-cart-link');
      if (!cartLink) return;
      cartLink.classList.remove('is-bumped');
      void cartLink.offsetWidth;
      cartLink.classList.add('is-bumped');
    };

    document.addEventListener('submit', async (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const button = form.querySelector('[name="add"], button[type="submit"], input[type="submit"]');
      if (button?.disabled || form.dataset.elfineSubmitting === 'true') return;
      form.dataset.elfineSubmitting = 'true';
      const originalText = button?.value || button?.textContent || 'Add to cart';
      const quantityField = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, Number.parseInt(quantityField?.value || '1', 10) || 1);
      const currentBadge = document.querySelector('[data-cart-count]');
      const previousCount = Number.parseInt(currentBadge?.textContent || '0', 10) || 0;
      updateCartCount(previousCount + quantity);
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
        if ('value' in button && button.tagName === 'INPUT') button.value = 'Adding…';
        else button.textContent = 'Adding…';
      }
      try {
        const root = window.Shopify?.routes?.root || '/';
        const addResponse = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!addResponse.ok) {
          const error = await addResponse.json();
          throw new Error(error.description || 'Unable to add this item.');
        }
        const cartResponse = await fetch(root + 'cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const cart = await cartResponse.json();
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        if (button) {
          if ('value' in button && button.tagName === 'INPUT') button.value = 'Added ✓';
          else button.textContent = 'Added ✓';
        }
        showCartToast();
      } catch (error) {
        updateCartCount(previousCount);
        if (button) {
          const message = error.message || 'Please try again';
          if ('value' in button && button.tagName === 'INPUT') button.value = message;
          else button.textContent = message;
        }
      } finally {
        if (button) button.classList.remove('is-loading');
        window.setTimeout(() => {
          if (button) {
            button.disabled = false;
            if ('value' in button && button.tagName === 'INPUT') button.value = originalText;
            else button.textContent = originalText;
          }
          delete form.dataset.elfineSubmitting;
        }, 1800);
      }
    }, true);

    // Mirror the store's 10% automatic discount on custom Elfine home product rows in all markets.
    document.querySelectorAll('.elfine-product-card__details > div > p').forEach((priceLine) => {
      if (priceLine.querySelector('.compare-at-price')) return;
      const original = priceLine.textContent.trim();
      const numberMatch = original.match(/[0-9][0-9,]*(?:\.[0-9]+)?/);
      if (!numberMatch) return;
      const amount = Number(numberMatch[0].replace(/,/g, ''));
      if (!Number.isFinite(amount) || amount <= 0) return;
      const currency = window.Shopify?.currency?.active || 'USD';
      let discounted;
      try {
        discounted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol'
        }).format(amount * 0.9);
      } catch (_) {
        discounted = original.replace(numberMatch[0], (amount * 0.9).toFixed(2));
      }
      const oldPrice = document.createElement('s');
      oldPrice.className = 'compare-at-price';
      oldPrice.textContent = original;
      const newPrice = document.createElement('span');
      newPrice.className = 'price';
      newPrice.textContent = discounted;
      priceLine.textContent = '';
      priceLine.append(oldPrice, document.createTextNode(' '), newPrice);
    });
  });
})();